import { fetchEventSource } from '@microsoft/fetch-event-source';

import { HTTP_TYPES } from '@/lib/enums';
import api from '@/api/api';
import { useUserStore } from '@/stores/user.store';
import type {
	DepotInsightsRequest,
	DepotInsightsResponse,
	RebuildProgressEvent,
	RebuildReviewCountsHandlers,
	RebuildReviewCountsRequest,
	TargetedRebuildRequest,
	TargetedRebuildResponse,
} from './types/reviews-profiles.types';

export type {
	ProfileInsightsDepth,
	InsightScopeKind,
	InsightScope,
	DepotInsightsRequest,
	DepotInsightsResponse,
	InsightsWindow,
	ReviewWindowMetrics,
	ProfileMetrics,
	RatingCount,
	MonthlyVolume,
	TopicSentiment,
	ProfileReviewInsight,
	InsightTheme,
	RebuildReviewCountsRequest,
	RebuildProgressEvent,
	RebuildReviewCountsHandlers,
	TargetedRebuildRequest,
	TargetedRebuildResponse,
	RebuildResult,
	RebuildOutcome,
	RebuildOutcomeStatus,
} from './types/reviews-profiles.types';

const API_VERSION = 'v1';

/**
 * True when a field carries a value. The rebuild stream distinguishes its event
 * kinds by which fields are populated, and it both omits fields and sends them
 * as null — so a bare `!== null` would read every absent field as present.
 */
function isSet(value: unknown): boolean {
	return value !== null && value !== undefined;
}

const response = {
	// This if for all endpoints of that type, not to be used unless really needed. Use the more specific ones below where possible.
	async postRequestLegacyReviews(payload: any): Promise<any> {
		// from_date: datetime
		// type: string

		return await api(
			HTTP_TYPES.POST,
			`${import.meta.env.VITE_DEPOT_API_REVIEWS}/profiles/request-legacy-reviews`,
			payload,
		);
	},
	async postFetchYearReviews(payload: any): Promise<any> {
		// profile_uuid: string=

		return await api(
			HTTP_TYPES.POST,
			`${import.meta.env.VITE_DEPOT_API_REVIEWS}/profiles/add-fetch-year-reviews`,
			payload,
		);
	},
	/**
	 * AI synopsis of a scope's reviews across a window.
	 *
	 * Note the singular `/profile/` — unlike its `/profiles/...` neighbours above —
	 * which is easy to "correct" into a 404, and which the scope selectors have
	 * outgrown: the endpoint analyses a list of profiles, a site, or a company.
	 *
	 * The payload must carry exactly ONE of `profile_uuids`, `site_uuid`, or
	 * `company_uuid`. Build it from an `InsightScope` — `useDepotInsightsStore()`
	 * does — rather than assembling the fields by hand.
	 *
	 * Takes ~10s at `initial` depth and ~20s at `deep` for a single profile. A
	 * whole company pools far more reviews and takes correspondingly longer, with
	 * no chunking available: one pooled corpus is the endpoint's purpose.
	 */
	async postProfileInsights(
		payload: DepotInsightsRequest,
	): Promise<{ data?: DepotInsightsResponse } | null> {
		return await api(
			HTTP_TYPES.POST,
			`${import.meta.env.VITE_DEPOT_API_REVIEWS}/reviews/profile/insights`,
			payload,
		);
	},
	/**
	 * Rebuild the review counts for named profiles — the targeted counterpart
	 * to the estate-wide stream below.
	 *
	 * Sends exactly ONE batch. Chunking a large list into batches that finish
	 * inside Azure API Management's 240s response ceiling is the store's job,
	 * so that ceiling has a single owner rather than being defended in two
	 * places. Callers wanting a whole company's profiles rebuilt should go
	 * through `useDepotAdminProfilesStore().rebuildReviewCounts`.
	 *
	 * A 200 does not mean every profile succeeded: the endpoint tolerates
	 * per-profile failures and reports them in `total_failed` and `results`.
	 */
	async rebuildReviewCounts(
		profileUuids: string[],
	): Promise<{ data?: TargetedRebuildResponse } | null> {
		// Always the plural form, even for one UUID — see TargetedRebuildRequest.
		const payload: TargetedRebuildRequest = { profile_uuids: profileUuids };

		return await api(
			HTTP_TYPES.POST,
			`${import.meta.env.VITE_DEPOT_API_REVIEWS}/profiles/rebuild-review-counts/`,
			payload,
			undefined,
			'application/json',
			API_VERSION,
		);
	},
	/**
	 * Rebuild every profile's total and per-month review counts, streamed as
	 * Server-Sent Events: one event per profile, then a terminal summary.
	 *
	 * Uses `fetchEventSource` rather than `api()`'s `onChunk` path because that
	 * path hands back raw byte-boundary chunks — a JSON event can be split
	 * across two of them. This library reassembles frames, and unlike native
	 * `EventSource` it can POST and send an `Authorization` header.
	 *
	 * The run is only alive while this connection is: aborting the signal, or
	 * closing the tab, stops it server-side.
	 */
	async rebuildAllReviewCounts(
		request: RebuildReviewCountsRequest,
		handlers: RebuildReviewCountsHandlers,
		signal?: AbortSignal,
	): Promise<void> {
		const userStore = useUserStore();
		const token = userStore.accessToken ?? (await userStore.ensureAccessToken(false));

		await fetchEventSource(
			// Dev/test stream straight at the container app; prod has no override, so it
			// falls back to APIM. ponytail: env var over a mode branch — the deploy already
			// sets these per GitHub environment.
			`${import.meta.env.VITE_DEPOT_API_REVIEWS_DIRECT || import.meta.env.VITE_DEPOT_API_REVIEWS}/profiles/rebuild-all-review-counts/`,
			{
				method: 'POST',
				signal,
				openWhenHidden: true,
				headers: {
					Authorization: token ? `Bearer ${token}` : '',
					'Content-Type': 'application/json',
					'X-Api-Version': API_VERSION,
					Accept: 'text/event-stream',
				},
				// `max_profiles` is omitted rather than sent as null, since the
				// endpoint reads its absence as "cover the whole estate".
				body: JSON.stringify({
					only_never_counted: request.only_never_counted ?? false,
					...(isSet(request.max_profiles) ? { max_profiles: request.max_profiles } : {}),
				}),
				async onopen(res) {
					const contentType = res.headers.get('content-type') ?? '';
					if (res.ok && contentType.includes('text/event-stream')) {
						return;
					}

					throw new Error(`Rebuild stream failed: ${res.status}`);
				},
				onmessage(ev) {
					if (!ev.data) {
						return;
					}

					const event = JSON.parse(ev.data) as RebuildProgressEvent;

					// Classified on the payload, not on `ev.event`: one shape
					// carries all three kinds and the server's event names are
					// not part of the contract. Summary is tested first because
					// it is terminal and must never be read as progress.
					if (isSet(event.completed)) {
						handlers.onSummary(event);
					} else if (isSet(event.error)) {
						handlers.onFailure(event);
					} else {
						handlers.onProgress(event);
					}
				},
				onerror(err) {
					// Rethrow to stop the library's automatic reconnect — a
					// reconnect would restart the whole run from the first
					// profile. The failure surfaces to the caller instead.
					throw err;
				},
			},
		);
	},
};

export default response;
